import { type UseMutationOptions, useMutation } from "@tanstack/react-query";
import { useCartStore } from "@/features/cart/components/cart-provider";
import { toProductItemInputs } from "@/features/cart/helpers/product-items";
import type {
  ConfigurationItem,
  ProductItemInput,
  ProductSelection,
} from "@/features/cart/types";
import type { Agreements } from "@/features/cart/types/agreements.type";
import type { Cart } from "@/features/cart/types/cart.type";
import type { OrderInvoice } from "@/features/cart/types/invoice-data.type";
import { ApiError } from "@/lib/helpers/errors";

export const CART_WRITE_SCOPE = "cart-write";

type UpdateCartPayload = {
  cartId?: string;
  item: ConfigurationItem;
  // Foodango products of the position (MPL-3829). Leave out when the write
  // does not touch them — the route keeps the ones the cart holds.
  productItems?: ProductSelection[];
};

// The cart as this client last saw it. The route falls back to it when the
// cart cannot be read because a product left the offer (422
// `product_items_not_priced` on GET /cart): it drops that product and writes
// the rest, instead of treating an unreadable cart as a lost one.
export type CartFallback = {
  items: ConfigurationItem[];
  productItems: ProductItemInput[];
  discountCode: string | null;
  agreements: Agreements;
  invoiceData: OrderInvoice | null;
};

type UpdateCartResponse = {
  cartId: string;
  cart: Cart;
  // Product the route had to drop to get the cart written (see CartFallback).
  droppedProductId?: string | null;
};

type CartErrorBody = {
  error?: string;
  message?: string | null;
};

// The route forwards the upstream body, so callers get both a human-readable
// message and the machine-readable code they may need to react to (an invalid
// sub-items selection has to be rolled back, see MPL-3694).
const getRequestError = async (response: Response) => {
  const data = (await response
    .json()
    .catch(() => null)) as CartErrorBody | null;

  return new ApiError(
    data?.message ?? data?.error ?? "Failed to update cart",
    response.status,
    data,
  );
};

export const useUpdateCart = (
  options?: Omit<
    UseMutationOptions<UpdateCartResponse, Error, UpdateCartPayload>,
    "mutationFn"
  >,
) => {
  const items = useCartStore((state) => state.items);
  const storedProductItems = useCartStore((state) => state.productItems);
  const discountCode = useCartStore((state) => state.discountCode);
  const agreements = useCartStore((state) => state.agreements);
  const invoiceData = useCartStore((state) => state.invoiceData);

  return useMutation({
    scope: { id: CART_WRITE_SCOPE },
    mutationFn: async ({ cartId, item, productItems }: UpdateCartPayload) => {
      const fallback: CartFallback = {
        items: items.map((cartItem) => cartItem.configurationItem),
        productItems: toProductItemInputs(storedProductItems),
        discountCode,
        agreements,
        invoiceData: invoiceData ?? null,
      };

      const response = await fetch("/api/cart/update", {
        method: "POST",
        body: JSON.stringify({
          cartId,
          item,
          productItems,
          fallback,
        }),
        keepalive: true,
      });

      if (!response.ok) {
        throw await getRequestError(response);
      }

      return (await response.json()) as UpdateCartResponse;
    },
    ...options,
  });
};
